import type Luminary from '@lumen5/luminary';
import type { Asset, Scene, SceneGroup } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import type { AssetType, SceneGroupType } from '../constants';
import { ABSENT, ASSET_TYPES, SCENE_GROUP_TYPES, UNREGISTERED } from '../constants';
import type {
    AssetDescription,
    BaseAssetDescription,
    CaptionsDescription,
    SceneCoverage,
    SceneDescription,
    SceneGroupDescription,
    SceneTransition,
    TranscriptDescription,
} from '../types';

import { formatDurationMs } from './assetFormatters';
import {
    formatIconColors,
    getIconIdFromAsset,
    renderColorAsString,
    renderColorSlot,
} from './assetHelpers';
import {
    type AssetTimingTranscriptContext,
    describeAssetAppearsAt,
} from './assetTimingDescription';
import { timeNarrationWords } from '../../html-design/sceneNarration';
import { describeHtmlComposition } from './htmlCompositionDescription';
import {
    getFullMediaResolution,
    getFullMediaScale,
    getFullMediaTranslation,
    getFullMediaTranslationRange,
    getMediaBrightness,
    getMediaIdFromAsset,
    getMediaTypeFromAsset,
    getVisibleMediaBounds,
    isWatermarkAsset,
} from './mediaHelpers';
import { getTextAndHighlightsAsMarkdown } from './textHelpers';

import { MediaRegistry } from 'js/src/agent/registries/MediaRegistry';
import type { ScenePartition } from 'js/src/agent/tools/transcript-partition';
import { partitionTranscriptByScene } from 'js/src/agent/tools/transcript-partition';
import { getInterleavedTranscript } from 'js/src/agent/tools/transcript-tokens';
import { AI_VOICE_SPEED_OPTIONS } from 'js/src/data/ai-voices-and-avatars/ai-voice-speeds';
import { getSceneDesign } from 'js/src/data/templates/templates-selectors';
import { roundToPrecision } from 'js/src/libs/math';
import type { RootState } from 'js/src/libs/RootState';
import {
    getVoiceDrivenAssetAIVoice,
    getVoiceDrivenAssetAIVoiceSpeed,
    getVoiceDrivenAssetPlaybackRegions,
    getVoiceDrivenBaseAssetSourceDuration,
    hasAppliedMatchCutWithNextScene,
    isAIAvatarSceneGroup,
    isAIVoiceoverSceneGroup,
    isTalkingHeadSceneGroup,
    isVoiceDrivenSceneGroup,
    isVoiceoverSceneGroup,
    translateVideoTimeToTranscriptTime,
} from 'js/src/luminary/luminary-selectors';

/**
 * The names a scene design flags as non-recommendable (waveforms, brand elements, …): assets a human
 * may edit but the recommender must leave alone. Empty without Redux state (the storeless offline-eval
 * path), where no scene-design data is available.
 */
export function getNonRecommendableAssetNames(scene: Scene, state?: RootState): Set<string> {
    // No scene-design data available (storeless offline-eval path, or a partial state without the
    // templates slice) → filter nothing, matching prior behavior.
    if (!state?.templates?.sceneDesigns) {
        return new Set<string>();
    }
    return new Set(getSceneDesign(state, scene.sceneDesign)?.non_recommendable_assets ?? []);
}

/**
 * Whether a named asset in this scene is non-recommendable per its scene design. The agent-facing
 * serialization omits these entirely and the Set* tools fail closed on them.
 */
export function isNonRecommendable(scene: Scene, name: string, state?: RootState): boolean {
    return getNonRecommendableAssetNames(scene, state).has(name);
}

/**
 * The styling `set_captions` can change, so the agent can confirm what it applied. The track's
 * underlying asset is a plain caption or a FancyCaptionAsset depending on the chosen design, so
 * every field is read defensively — a design without spoken-word highlighting has no such style.
 */
function describeCaptionStyling(
    asset: Extract<SceneGroup, { captions: unknown }>['captions']['asset'],
    includeColorNames: boolean,
): { fontSize: number; textColor: string; spokenWordHighlightColor?: string; font: string } {
    // Serialization runs on every state capture, so nothing here may throw: a caption design may
    // be backed by a plain caption or a FancyCaptionAsset, and a partially-built asset can be
    // missing its styles entirely.
    const normalStyle = asset.normalStyle as typeof asset.normalStyle | undefined;
    const baseColor = normalStyle?.fill;
    const spokenWordColor =
        VideoData.isFancyCaptionAsset(asset) && asset.isSpokenWordStyleEnabled
            ? asset.spokenWordStyle?.fill
            : undefined;

    const textColor =
        typeof baseColor === 'number' ? renderColorAsString(baseColor, includeColorNames) : ABSENT;

    // Omitted rather than reported as absent: the highlight only exists on designs that enable it,
    // so a missing property distinguishes "this design has no highlight" from "it has one, unset".
    const spokenWordHighlightColor =
        typeof spokenWordColor === 'number' && spokenWordColor !== baseColor
            ? renderColorAsString(spokenWordColor, includeColorNames)
            : undefined;

    const font = (() => {
        try {
            return (
                normalStyle
                    ?.getRendererSettings({
                        leading: normalStyle.leading,
                        fontSize: normalStyle.fontSize,
                    })
                    ?.fontFamily?.split('-')[0] ?? ABSENT
            );
        } catch {
            return ABSENT;
        }
    })();

    return {
        fontSize: Math.round(asset.textSize ?? 0),
        textColor,
        ...(spokenWordHighlightColor !== undefined ? { spokenWordHighlightColor } : {}),
        font,
    };
}

/**
 * What a voice-driven scene's transcript tells the description: which words the scene owns and its
 * role over them (`coverage`), and the context for naming its assets' intro times as words
 * (`timing`). Both come from one read of the shared transcript partition, so a scene's reported
 * words match what add_voiceover_scene covers by construction and an asset's `appearsAt` word is
 * drawn from the same tokens. Undefined when the scene has no transcript (text-on-media, etc.) or
 * no Redux state is available.
 */
function getSceneTranscriptDescription(
    scene: Scene,
    state: RootState | undefined,
): { coverage: SceneCoverage; timing: AssetTimingTranscriptContext } | undefined {
    const sceneGroup = scene.sceneGroup;
    if (!state || !sceneGroup) {
        return undefined;
    }
    const partition = partitionTranscriptByScene(state, sceneGroup);
    const entry = partition?.find((part) => part.scene.id === scene.id);
    if (!partition || !entry) {
        return undefined;
    }
    const cues = cueCoveredWords(scene, entry);
    const playbackRegions = getVoiceDrivenAssetPlaybackRegions(sceneGroup);
    const baseAssetSourceDuration = getVoiceDrivenBaseAssetSourceDuration(sceneGroup);
    return {
        coverage: {
            words: entry.words.join(' '),
            ...(cues !== null ? { cues } : {}),
            role: entry.role,
        },
        timing: {
            tokens: partition.flatMap((part) => part.tokens),
            videoTimeToTranscriptTime: (videoTimeMs) =>
                translateVideoTimeToTranscriptTime(
                    videoTimeMs,
                    playbackRegions,
                    baseAssetSourceDuration,
                    sceneGroup.startTime,
                ),
        },
    };
}

/**
 * The scene's covered words with the second each spoken one lands on. Kept apart from `words`,
 * which the agent copies verbatim into `start_words` and `add_voiceover_scene` — a cue prefix in a
 * word it copies would not match the transcript. Pause tags keep their rendered form, since a
 * silence has no onset to sync to, and it falls back to the bare words when the group has no
 * timings to map against: a wrong second is worse than none.
 */
function cueCoveredWords(scene: Scene, entry: ScenePartition): string | null {
    const timed = timeNarrationWords(scene, entry.tokens);
    if (!timed || timed.length !== entry.tokens.length) {
        return null;
    }
    const cued = entry.tokens
        .map((token, index) =>
            token.isPause
                ? null
                : `[${timed[index].startSeconds.toFixed(1)}s] ${entry.words[index]}`,
        )
        .filter((cue): cue is string => cue !== null);
    return cued.length > 0 ? cued.join(' ') : null;
}

export const simplifyAssetType = (asset: Asset): AssetType => {
    // Handle stickers first - they are a special case of media assets
    if (VideoData.isMediaAsset(asset) && VideoData.isStickerAsset(asset)) {
        return ASSET_TYPES.STICKER;
    }

    // Watermarks are ordinary media assets identified only by a reserved name (Luminary has no
    // watermark type), so classify them before falling through to the class-type switch.
    if (isWatermarkAsset(asset)) {
        return ASSET_TYPES.WATERMARK;
    }

    // Then check classType for regular asset types
    switch (asset.classType) {
        case VideoData.TEXT_ASSET_TYPE:
            if (!asset.isEditable) {
                return ASSET_TYPES.READONLY_TEXT;
            }
            return ASSET_TYPES.TEXT;
        case VideoData.FANCY_CAPTION_ASSET_TYPE:
            return ASSET_TYPES.FANCY_CAPTION;
        case VideoData.CAPTION_ASSET_TYPE:
            return ASSET_TYPES.CAPTION;
        case VideoData.MEDIA_ASSET_TYPE:
            if (!asset.isEditable) {
                return ASSET_TYPES.READONLY_MEDIA;
            }
            return ASSET_TYPES.MEDIA;
        case VideoData.ICON_ASSET_TYPE:
            if (!asset.isEditable) {
                return ASSET_TYPES.READONLY_ICON;
            }
            return ASSET_TYPES.ICON;
        case VideoData.COLOR_ASSET_TYPE:
            return ASSET_TYPES.BACKGROUND;
        case VideoData.CHART_ASSET_TYPE:
            return ASSET_TYPES.CHART;
        case VideoData.HTML_ASSET_TYPE:
            return ASSET_TYPES.MOTION_GRAPHIC;
        case VideoData.GROUP_ASSET_TYPE:
            return ASSET_TYPES.GROUP;
        default:
            return asset.classType as AssetType;
    }
};

/**
 * Apply asset-specific information based on asset type.
 * Uses early returns with proper type guards for type safety.
 */
function applyAssetSpecificInfo(
    asset: Asset,
    assetInfo: BaseAssetDescription,
    includeColorNames: boolean = true,
): AssetDescription {
    // Watermark assets are special media assets managed on the scene level through SceneWatermarkManager
    if (isWatermarkAsset(asset)) {
        return {
            ...assetInfo,
            type: ASSET_TYPES.WATERMARK,
        };
    }

    // Color/Background assets
    if (VideoData.isColorAsset(asset)) {
        return {
            ...assetInfo,
            type: ASSET_TYPES.BACKGROUND,
            color: `${renderColorAsString(asset.sourceColor, includeColorNames)}${renderColorSlot(asset, 'sourceColor')}`,
            ...(asset.sourceAlpha < 1 ? { opacity: roundToPrecision(asset.sourceAlpha, 2) } : {}),
        };
    }

    // Group assets
    if (VideoData.isAssetGroup(asset)) {
        const children = asset
            .getAssets()
            .map((child) => child.name)
            .join(', ');
        return {
            ...assetInfo,
            type: ASSET_TYPES.GROUP,
            children,
            childrenAlignment: asset.alignChildren,
        };
    }

    // Chart assets: surface the chart type and data so the agent can see what the chart
    // shows (without this they'd fall through to the bare-classType default).
    if (VideoData.isChartAsset(asset)) {
        return {
            ...assetInfo,
            type: ASSET_TYPES.CHART,
            chartType: asset.chartType || 'unknown',
            chartData: asset.chartData ?? '',
        };
    }

    // Generated (HTML) graphics: describe the composition by its addressable parts and editable
    // copy. The document itself is never serialized.
    if (VideoData.isHtmlAsset(asset)) {
        return {
            ...assetInfo,
            type: ASSET_TYPES.MOTION_GRAPHIC,
            ...describeHtmlComposition(asset.html ?? ''),
        };
    }

    // Icon assets
    if (VideoData.isIconAsset(asset)) {
        return {
            ...assetInfo,
            type: simplifyAssetType(asset),
            mediaId: getIconIdFromAsset(asset),
            iconColors: formatIconColors(asset, includeColorNames),
        };
    }

    // Fancy Caption assets (TEXT type variant). We deliberately omit the caption text and line
    // count: the text is a render-time slice of the transcript the agent already reads in full via
    // the scene group's transcript, and both are only readable off the live renderer (it throws /
    // reports 0 when the caption isn't currently rendered) — so emitting them added redundancy and
    // made the same caption serialize differently depending on render state.
    if (VideoData.isFancyCaptionAsset(asset)) {
        const textColors = asset.normalStyle.fill;
        const fontSize = Math.round(asset.textSize);
        const fontFamily = asset.normalStyle
            .getRendererSettings({
                leading: asset.normalStyle.leading,
                fontSize: asset.normalStyle.fontSize,
            })
            .fontFamily.split('-')[0];

        return {
            ...assetInfo,
            type: ASSET_TYPES.FANCY_CAPTION,
            alignment: asset.textAlign,
            colors: renderColorAsString(textColors, includeColorNames),
            fontSize,
            font: fontFamily,
        };
    }

    // Text assets
    if (VideoData.isTextAsset(asset)) {
        const baseColor = asset.normalStyle.fill;
        const highlightColor = asset.highlightStyle.fill;
        const baseSlot = renderColorSlot(asset, 'normalStyle.fill');
        const textColors =
            highlightColor !== baseColor && asset.textHighlights.length > 0
                ? `${renderColorAsString(baseColor, includeColorNames)}${baseSlot} and ${renderColorAsString(highlightColor, includeColorNames)}${renderColorSlot(asset, 'highlightStyle.fill')}`
                : `${renderColorAsString(baseColor, includeColorNames)}${baseSlot}`;
        const textLines = asset?.renderer?.metrics?.lines?.length ?? 0;

        return {
            ...assetInfo,
            type: simplifyAssetType(asset),
            text: getTextAndHighlightsAsMarkdown(asset),
            alignment: asset.textAlign,
            colors: textColors,
            lines: textLines,
            fontSize: Math.round(asset.textSize),
            font: asset.normalStyle.getRendererSettings({
                leading: asset.normalStyle.leading,
                fontSize: asset.normalStyle.fontSize,
            }).fontFamily,
        };
    }

    // Sticker assets
    if (VideoData.isMediaAsset(asset) && VideoData.isStickerAsset(asset)) {
        return {
            ...assetInfo,
            type: ASSET_TYPES.STICKER,
            mediaId: getMediaIdFromAsset(asset),
        };
    }

    // Media assets (images/videos)
    if (VideoData.isMediaAsset(asset) && asset.sourceData) {
        const mediaRegistry = MediaRegistry.getInstance();
        const mediaId = getMediaIdFromAsset(asset);
        // The asset's own source title is what a viewer of the video data can see; the registry's
        // representation (below) refines it when the media has been looked up in this session.
        let mediaDescription = asset.sourceData.title || 'No description available';
        // Media type comes from the asset itself (isVideo), never inferred from a failed registry
        // lookup — so an unregistered video is reported as a video, not defaulted to an image.
        let mediaType: string = getMediaTypeFromAsset(asset);

        if (mediaId !== ABSENT && mediaId !== UNREGISTERED) {
            const representation = mediaRegistry.getRepresentation(mediaId);
            if (representation) {
                mediaDescription =
                    representation.title ||
                    representation.description ||
                    'No description available';
                mediaType = representation.type;
            }
        }

        const bounds = getVisibleMediaBounds(asset);
        const mediaResolution = getFullMediaResolution(asset);
        const mediaScale = getFullMediaScale(asset);
        const mediaTranslation = getFullMediaTranslation(asset);
        const { mediaXTranslationRange, mediaYTranslationRange } =
            getFullMediaTranslationRange(asset);

        return {
            ...assetInfo,
            type: simplifyAssetType(asset),
            mediaId,
            description: mediaDescription,
            mediaType,
            // The on-canvas placement of the media box. Read from transformation.position
            // (not getBounds), because getBounds is the full scaled media box — it shifts whenever a
            // differently-sized clip is swapped in, reporting a phantom move the viewer never sees.
            position: `${Math.round(asset.transformation.position.x)} ${Math.round(asset.transformation.position.y)}`,
            bounds,
            mediaResolution,
            mediaScale,
            mediaTranslation,
            mediaXTranslationRange,
            mediaYTranslationRange,
            brightness: getMediaBrightness(asset),
        };
    }

    // Fallback: return base asset info
    return assetInfo;
}

/**
 * The scene groups captions can apply to: the voice-driven ones.
 *
 * Deliberately NOT narrowed to groups with transcript words: `originalTranscript` is the persisted
 * copy and stays empty until the lexical editor syncs, so a live voice-driven group routinely
 * reports zero words while its captions render fine.
 */
function captionableSceneGroups(luminary: Luminary): SceneGroup[] {
    return luminary.sceneGroups.filter(isVoiceDrivenSceneGroup);
}

/**
 * Whether the global caption track renders on this scene. `undefined` when the question doesn't
 * apply — a scene outside a voice-driven group, or one whose group has captions turned off — so
 * the field is omitted rather than reported as a `false` the agent could mistake for a per-scene
 * override it can flip.
 *
 * A scene rendering its own caption suppresses the global track (`scene.displayCaptions`), so
 * that flag is the per-scene answer.
 */
function describeSceneCaptionsVisibility(scene: Scene): boolean | undefined {
    const sceneGroup = scene.sceneGroup;
    if (!sceneGroup || !isVoiceDrivenSceneGroup(sceneGroup) || !sceneGroup.captions?.visible) {
        return undefined;
    }
    return Boolean(scene.displayCaptions);
}

/**
 * The video's caption track, serialized once. Captions are global: `set_captions` writes the
 * design, size, colors and font across every voice-driven scene group at once, so an identical
 * caption asset used to be repeated on every scene. It lives here instead; the per-scene fact
 * (does the track render on this scene) is `SceneDescription.captionsVisible`.
 *
 * Styling is read off the FIRST voice-driven group with a visible track — the tool writes the same
 * values to all of them, so any one of them describes the video.
 */
export const getCaptionsDescription = (
    luminary: Luminary,
    includeColorNames: boolean = true,
): CaptionsDescription | undefined => {
    // A video with no voice-driven scene group has nothing to caption, so it gets no captions
    // block at all: "captions are off" would imply a track the agent could turn on, when the real
    // answer is that captions don't apply to this video.
    const captionable = captionableSceneGroups(luminary);
    if (captionable.length === 0) {
        return undefined;
    }

    // Every scene group carries a CaptionsManager whose asset always exists, so gate on captions
    // actually being in use (visible) to avoid describing a track nobody sees.
    const captioned = captionable.find(
        (sceneGroup) => sceneGroup.captions?.visible && sceneGroup.captions.asset,
    );

    if (!captioned) {
        return { enabled: false };
    }

    const asset = captioned.captions.asset;

    // Bounds come from approximateMaxBounds (the design-anchored max text area), NOT
    // asset.getBounds(): getBounds() is the tight box of whatever caption words are on screen at
    // the renderer's current frame, so it changes with the playhead and made the same caption
    // serialize differently on every capture — a phantom "captions moved" diff on essentially
    // every tool call. approximateMaxBounds is render-state-independent.
    //
    // Read it off the caption ASSET, not the track: the track returns an empty rectangle whenever
    // the caption asset's transcript is empty, which reported every caption as "0 0 0 0".
    const maxBounds = asset.approximateMaxBounds;

    return {
        enabled: true,
        sceneDesignId: captioned.captions.sceneDesignId,
        bounds: `${Math.round(maxBounds.x)} ${Math.round(maxBounds.y)} ${Math.round(maxBounds.width)} ${Math.round(maxBounds.height)}`,
        ...describeCaptionStyling(asset, includeColorNames),
    };
};

/**
 * Whether the scene has anything a text position preset could move: an editable text asset or an
 * animated (fancy) caption. Searched deep, so text nested inside an asset group or a subscene counts.
 */
const hasMovableTextAsset = (scene: Scene): boolean =>
    [VideoData.TEXT_ASSET_TYPE, VideoData.FANCY_CAPTION_ASSET_TYPE].some((assetType) =>
        scene.getAssetsByType(assetType).some((asset) => asset.isEditable),
    );

export const getSceneDescriptions = (
    scene: Scene,
    luminary: Luminary,
    state?: RootState,
    includeColorNames: boolean = true,
    omitNonRecommendable: boolean = false,
): SceneDescription => {
    const hasSubscenes = scene.subscenes.length > 1;
    // Text position presets only reposition text, so a scene with nothing movable (a pure media/icon
    // scene, or one whose only text is locked by the design) has no actionable preset to offer, and
    // reporting them invited the agent to call set_text_position_preset for no effect. Only the
    // single-preset case is suppressed: a scene offering several is a real choice worth surfacing
    // even when we can't see what the preset would move.
    const textPositionPresets =
        scene.availableTextPositions.length === 1 && !hasMovableTextAsset(scene)
            ? []
            : scene.availableTextPositions;

    // Assets the scene design marks as non-recommendable are omitted only for the recommender-facing
    // read paths (the general agent's video state and scene screenshots), which opt in via
    // omitNonRecommendable so the agent stays unaware of them and never targets them. Consumers that
    // list assets for collision-avoidance rather than editing (e.g. the HTML-design/Codeo context)
    // leave the flag off and keep seeing every asset. Empty when the flag is off, matching the
    // storeless offline-eval path (no state → nothing to look up).
    const nonRecommendable = omitNonRecommendable
        ? getNonRecommendableAssetNames(scene, state)
        : new Set<string>();

    // Find which scene group this scene belongs to
    const sceneGroupIndex = luminary.sceneGroups.findIndex((sg) =>
        sg.scenes.some((s) => s.id === scene.id),
    );

    // Coverage needs Redux state (to tell overlay from base scenes). The storeless
    // description path (generateVideoDescriptionFromVideoData) passes no state, so it is omitted.
    const transcriptDescription = getSceneTranscriptDescription(scene, state);
    const coverage = transcriptDescription?.coverage;
    const timingContext = transcriptDescription?.timing;

    // Captions are NOT serialized here. They are a single global track — `set_captions` applies
    // the design, size, colors and font across every voice-driven group at once — so repeating an
    // identical caption asset on every scene was pure duplication. The track serializes once at the
    // video level (see getCaptionsDescription); all that is per-scene is whether it renders here.
    const captionsVisible = describeSceneCaptionsVisibility(scene);

    const transition: SceneTransition | undefined = hasAppliedMatchCutWithNextScene(scene)
        ? 'match-cut'
        : undefined;

    // Match-cut stand-ins are transient copies excluded from save, so an edit aimed at one is lost.
    const shouldSerializeAsset = (asset: Asset): boolean =>
        !VideoData.isLottieAsset(asset) &&
        !nonRecommendable.has(asset.name) &&
        !asset.isMatchCutDuplicate;

    if (hasSubscenes) {
        // For scenes with subscenes, collect assets from each subscene
        const subsceneAssets: (AssetDescription & { subscene: number })[] = [];

        for (let subsceneIndex = 0; subsceneIndex < scene.subscenes.length; subsceneIndex++) {
            const subscene = scene.subscenes[subsceneIndex];
            const subsceneAssetsData = subscene
                .getAssets()
                .filter(shouldSerializeAsset)
                .map((asset) => {
                    const appearsAt = describeAssetAppearsAt(asset, timingContext);
                    const assetInfo: BaseAssetDescription & { subscene: number } = {
                        name: asset.name,
                        type: simplifyAssetType(asset),
                        bounds: `${Math.round(asset.getBounds().x)} ${Math.round(asset.getBounds().y)} ${Math.round(asset.getBounds().width)} ${Math.round(asset.getBounds().height)}`,
                        subscene: subsceneIndex,
                        ...(appearsAt ? { appearsAt } : {}),
                    };

                    // Apply the same asset-specific logic as the non-subscene case
                    const updatedAssetInfo = applyAssetSpecificInfo(
                        asset,
                        assetInfo,
                        includeColorNames,
                    );
                    return { ...updatedAssetInfo, subscene: subsceneIndex };
                });
            subsceneAssets.push(...subsceneAssetsData);
        }

        return {
            id: scene.id,
            globalIndex: scene.globalIndex,
            sceneGroupIndex,
            design: scene.sceneDesign,
            ...(scene.sceneCategory ? { sceneCategory: scene.sceneCategory } : {}),
            duration: formatDurationMs(scene.duration),
            subscenes: scene.subscenes.length,
            ...(coverage ? { coverage } : {}),
            ...(captionsVisible !== undefined ? { captionsVisible } : {}),
            ...(transition ? { transition } : {}),
            assets: subsceneAssets,
            textPositionPresets,
        };
    } else {
        // For scenes without subscenes, use the original logic
        const assets = scene
            .getAssets()
            .filter(shouldSerializeAsset)
            .map((asset) => {
                const appearsAt = describeAssetAppearsAt(asset, timingContext);
                const assetInfo: BaseAssetDescription = {
                    name: asset.name,
                    type: simplifyAssetType(asset),
                    bounds: `${Math.round(asset.getBounds().x)} ${Math.round(asset.getBounds().y)} ${Math.round(asset.getBounds().width)} ${Math.round(asset.getBounds().height)}`,
                    ...(appearsAt ? { appearsAt } : {}),
                };

                return applyAssetSpecificInfo(asset, assetInfo, includeColorNames);
            });

        return {
            id: scene.id,
            globalIndex: scene.globalIndex,
            sceneGroupIndex,
            design: scene.sceneDesign,
            ...(scene.sceneCategory ? { sceneCategory: scene.sceneCategory } : {}),
            duration: formatDurationMs(scene.duration),
            subscenes: 1,
            ...(coverage ? { coverage } : {}),
            ...(captionsVisible !== undefined ? { captionsVisible } : {}),
            ...(transition ? { transition } : {}),
            assets,
            textPositionPresets,
        };
    }
};

// Scene Group serialization functions

export function getSceneGroupType(sceneGroup: SceneGroup): SceneGroupType {
    if (isTalkingHeadSceneGroup(sceneGroup)) return SCENE_GROUP_TYPES.RECORDED_TALKING_HEAD;
    if (isAIAvatarSceneGroup(sceneGroup)) return SCENE_GROUP_TYPES.AI_TALKING_HEAD;
    if (isVoiceoverSceneGroup(sceneGroup)) {
        return isAIVoiceoverSceneGroup(sceneGroup)
            ? SCENE_GROUP_TYPES.AI_VOICEOVER
            : SCENE_GROUP_TYPES.RECORDED_VOICEOVER;
    }
    return SCENE_GROUP_TYPES.REGULAR;
}

/**
 * The agent-facing transcript for a scene group: the canonical interleaved (word + pause) token
 * list, read from the single shared builder so the read view and the word-driven placement tools
 * consume provably identical tokens. Returns null for groups with no transcript.
 */
function getTranscriptDescription(
    sceneGroup: SceneGroup,
    state?: RootState,
): TranscriptDescription | null {
    const transcriptData = sceneGroup.originalTranscript?.transcriptData;
    if (!transcriptData?.words?.length) {
        return null;
    }

    return {
        language: transcriptData.language,
        words: getInterleavedTranscript(state, sceneGroup),
    };
}

/**
 * The AI voice and speed for an AI-voiceover scene group — surfaced so the agent can see (and diff)
 * what edit_ai_voiceover would change. Both come off the Luminary asset's source data (no Redux
 * needed). The stored speed is a multiplier; map it back to its named step (slowest…fastest) so the
 * read vocabulary matches what edit_ai_voiceover accepts. Empty for non-AI-voiceover groups.
 */
function getAIVoiceInfo(
    sceneGroup: SceneGroup,
): Pick<SceneGroupDescription, 'voice' | 'voiceSpeed'> {
    if (!isAIVoiceoverSceneGroup(sceneGroup)) {
        return {};
    }
    const voice = getVoiceDrivenAssetAIVoice(sceneGroup);
    const speedValue = getVoiceDrivenAssetAIVoiceSpeed(sceneGroup);
    const namedStep = AI_VOICE_SPEED_OPTIONS.find((option) => option.value === speedValue);
    const voiceSpeed = namedStep?.name ?? (speedValue != null ? `${speedValue}x (custom)` : null);
    return { voice, voiceSpeed };
}

export function getSceneGroupDescriptions(
    luminary: Luminary,
    state?: RootState,
): SceneGroupDescription[] {
    return luminary.sceneGroups.map((sceneGroup, index) => {
        const type = getSceneGroupType(sceneGroup);
        const sceneIndices = sceneGroup.scenes.map((scene) =>
            luminary.scenes.findIndex((s) => s.id === scene.id),
        );

        return {
            id: sceneGroup.id,
            index,
            type,
            sceneIndices,
            duration: formatDurationMs(sceneGroup.duration),
            transcript: getTranscriptDescription(sceneGroup, state),
            ...getAIVoiceInfo(sceneGroup),
        };
    });
}
